import { useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useHasAllotment } from '../hooks/use-has-allotment';
import { MultiCampaignButton } from './multi-campaign-button';
import { SingleCampaignButton } from './single-campaign-button';
import { SubscribeButton } from './subscribe-button';

type NewCampaignButtonProps = {
  /** An undefined campaign type means both email and text campaigns should be handled, ie: the drafts page */
  campaignType?: CampaignType;
  selectedLocationIds: string[];
  trackingIdPrefix: string;
};

export const NewCampaignButton = ({ campaignType, selectedLocationIds, trackingIdPrefix }: NewCampaignButtonProps) => {
  const {
    hasAllotment: hasEmailAllotment,
    isError: isHasEmailAllotmentError,
    isLoading: isHasEmailAllotmentLoading,
  } = useHasAllotment({
    campaignType: CampaignType.EMAIL,
    enabled: campaignType === CampaignType.EMAIL || !campaignType,
    selectedLocationIds,
  });

  const {
    hasAllotment: hasTextAllotment,
    isError: isHasTextAllotmentError,
    isLoading: isHasTextAllotmentLoading,
  } = useHasAllotment({
    campaignType: CampaignType.SMS,
    enabled: campaignType === CampaignType.SMS || !campaignType,
    selectedLocationIds,
  });

  const hasAllotment = hasEmailAllotment || hasTextAllotment;

  const isError = isHasEmailAllotmentError || isHasTextAllotmentError;
  const isLoading = isHasEmailAllotmentLoading || isHasTextAllotmentLoading;

  const effectiveCampaignType = useMemo(() => {
    // If a specific campaign type was requested, show it even if there is no allotment
    if (campaignType !== undefined) {
      return campaignType;
    }

    // The user has an allotment for both campaign types. Show the popover with both buttons.
    if (hasEmailAllotment && hasTextAllotment) {
      return undefined;
    }

    // The user only has an allotment for email campaigns. Show the email campaigns button.
    if (hasEmailAllotment) {
      return CampaignType.EMAIL;
    }

    // The user only has an allotment for text campaigns. Show the text campaigns button.
    if (hasTextAllotment) {
      return CampaignType.SMS;
    }

    // The user doesn't have an allotment. Default to undefined and show the unsubscribe button.
    return undefined;
  }, [campaignType, hasEmailAllotment, hasTextAllotment]);

  if (isLoading || isError) {
    return <SingleCampaignButton campaignType={effectiveCampaignType} disabled trackingIdPrefix={trackingIdPrefix} />;
  }

  if (!hasAllotment) {
    return <SubscribeButton campaignType={effectiveCampaignType} trackingIdPrefix={trackingIdPrefix} />;
  }

  if (effectiveCampaignType === undefined) {
    return <MultiCampaignButton trackingIdPrefix={trackingIdPrefix} />;
  }

  return (
    <SingleCampaignButton
      campaignType={effectiveCampaignType}
      disabled={!hasAllotment}
      trackingIdPrefix={trackingIdPrefix}
    />
  );
};
